import clsx from 'clsx'
import { useEffect, useRef, useState, type ComponentPropsWithoutRef } from 'react'

import { isVisualTest } from '@lib/utils/test-detection'

import { getPictureAttributes } from '@components/contentful-content-types/asset-image/utils'
import { type LayoutColumn } from '@components/layout/types'
import { Picture } from '@components/ui-core/picture/picture'

import styles from './asset-grid.module.scss'

export type Asset = {
  id: string
  url: string
  width: number
  height: number
  isSvg: boolean
  isRounded?: boolean
  alt: string
}

interface AssetGridProps extends ComponentPropsWithoutRef<'div'> {
  assets: (Asset | undefined)[]
}

const GRID_CELLS = 6
const PAUSE_DURATION = 2000

// Create cells array outside component to avoid recreation on every render
const CELLS = Array.from({ length: GRID_CELLS }, (_, i) => i)

/**
 * This element displays a grid of assets that are animated
 * fading in and out
 */
export const AssetGrid = ({ assets }: AssetGridProps) => {
  // Validate assets to prevent modulo by zero
  const assetCount = assets?.length ?? 0

  const [currentIndices, setCurrentIndices] = useState(
    Array.from({ length: GRID_CELLS }, (_, i) => (assetCount > 0 ? i % assetCount : 0)),
  )
  const [emptyingCells, setEmptyingCells] = useState(new Set())
  const [fadingOutCells, setFadingOutCells] = useState(new Set())
  const [lastChangedCell, setLastChangedCell] = useState(-1)

  // Use refs to avoid stale closures
  const lastChangedCellRef = useRef(lastChangedCell)
  const currentIndicesRef = useRef(currentIndices)
  const timeoutsRef = useRef<Set<NodeJS.Timeout>>(new Set())

  // Update refs when state changes
  lastChangedCellRef.current = lastChangedCell
  currentIndicesRef.current = currentIndices

  useEffect(() => {
    // Don't use this effect if there aren't enough assets
    if (assetCount <= GRID_CELLS || isVisualTest()) {
      return
    }

    // Capture the current timeouts set for proper cleanup
    const timeouts = timeoutsRef.current

    // Clear any existing timeouts
    for (const timeout of timeouts) {
      clearTimeout(timeout)
    }
    timeouts.clear()

    const animateCell = () => {
      // Pick random cell
      const cellsWithoutLastUpdated = Array.from({ length: GRID_CELLS }, (_, i) => i).filter(
        i => i !== lastChangedCellRef.current,
      )
      const randomDormantCell =
        cellsWithoutLastUpdated[Math.floor(Math.random() * cellsWithoutLastUpdated.length)]
      setLastChangedCell(randomDormantCell)

      // Start fading out the asset
      setFadingOutCells(prev => new Set(prev).add(randomDormantCell))

      // After fade out completes, remove asset completely
      const fadeOutTimeout = setTimeout(() => {
        setFadingOutCells(prev => {
          const next = new Set(prev)
          next.delete(randomDormantCell)
          return next
        })
        setEmptyingCells(prev => new Set(prev).add(randomDormantCell))
      }, 500)
      timeouts.add(fadeOutTimeout)

      // After empty delay, bring in next asset
      const nextAssetTimeout = setTimeout(() => {
        // Calculate next asset index using functional update to avoid stale state
        setCurrentIndices(prevIndices => {
          const visibleAssets = new Set(prevIndices.filter((_, i) => i !== randomDormantCell))
          let nextIndex = (prevIndices[randomDormantCell] + 1) % assetCount
          let attempts = 0

          // Search for an asset not currently visible, with a hard limit
          const maxAttempts = assetCount
          while (visibleAssets.has(nextIndex) && attempts < maxAttempts) {
            nextIndex = (nextIndex + 1) % assetCount
            attempts++
          }

          return prevIndices.map((index, i) => (i === randomDormantCell ? nextIndex : index))
        })

        setEmptyingCells(prev => {
          const next = new Set(prev)
          next.delete(randomDormantCell)
          return next
        })
      }, 1000)
      timeouts.add(nextAssetTimeout)
    }

    // Start first animation immediately after a brief delay
    const initialTimeout = setTimeout(animateCell, 1000)
    timeouts.add(initialTimeout)

    // Then continue with regular interval
    const interval = setInterval(animateCell, PAUSE_DURATION)
    timeouts.add(interval)

    return () => {
      clearInterval(interval)
      // Clean up all pending timeouts
      for (const timeout of timeouts) {
        clearTimeout(timeout)
      }
      timeouts.clear()
    }
  }, [assets, assetCount])

  // Early return if no assets to prevent modulo by zero
  if (assetCount === 0) {
    return null
  }

  return (
    <div className={styles.container}>
      {CELLS.map(index => {
        const isEmptying = emptyingCells.has(index)
        const isFadingOut = fadingOutCells.has(index)
        const currentCellAssetIndex = currentIndices[index]
        // Ensure asset index is within bounds and asset exists
        const asset = currentCellAssetIndex < assetCount ? assets[currentCellAssetIndex] : undefined

        const { src, width, height, srcSets, sizes } = getPictureAttributes({
          width: asset?.width ?? 0,
          height: asset?.height ?? 0,
          url: asset?.url ?? '',
          isSvg: asset?.isSvg ?? false,
          cols: (12 / GRID_CELLS) as LayoutColumn,
        })

        return (
          <div key={`asset-grid-cell-${index}`} className={styles.cell}>
            {asset && !isEmptying && (
              <Picture
                className={clsx(
                  styles.asset,
                  isFadingOut ? styles.fadeOut : styles.fadeIn,
                  asset.isRounded ? styles.roundedImage : '',
                )}
                alt={asset.alt}
                src={src}
                height={height}
                width={width}
                srcSets={srcSets}
                sizes={sizes}
                responsive={!asset.isSvg}
                fit="contain"
                loading="lazy"
                fetchPriority="low"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}
